import {Color} from '@benchling/nucleus-ui';
import {UnstyledButton, createStyles} from '@mantine/core';
import React from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const useStyles = createStyles((theme) => ({
  root: {
    paddingBottom: theme.spacing.sm,
  },
  sectionHeader: {
    padding: '6px 16px',
    fontSize: 11,
    fontWeight: 600,
    lineHeight: '16.5px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.55px',
    color: '#868e96',
  },
  grid: {
    padding: '0 16px',
  },
  cardIcon: {
    width: 36,
    height: 36,
    borderRadius: theme.radius.sm,
    padding: theme.spacing.xs,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  cardInner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.md,
    width: '100%',
    height: 36,
  },
  cardTextWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing.xs,
    overflow: 'hidden',
  },
  cardTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardSubtitle: {
    fontSize: 12,
    color: theme.colors.gray[9],
    lineHeight: '14px',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

interface QuickActionCardProps {
  icon: JSX.Element;
  iconBgColor: string;
  iconColor: string;
  title: string;
  subtitle: string;
  onClick: () => void;
}

function QuickActionCard({
  icon,
  iconBgColor,
  iconColor,
  title,
  subtitle,
  onClick,
}: QuickActionCardProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <UnstyledButton
      onClick={onClick}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        height: 60,
        padding: theme.spacing.md,
        borderRadius: theme.radius.sm,
        border: `1px solid ${theme.colors.gray[2]}`,
        transition: 'all 100ms ease',
        '&:hover': {
          backgroundColor: theme.colors.gray[0],
          borderColor: theme.colors.gray[3],
        },
      })}
    >
      <div className={classes.cardInner}>
        <div
          className={classes.cardIcon}
          style={{backgroundColor: iconBgColor, color: iconColor}}
          aria-hidden="true"
        >
          {icon}
        </div>
        <div className={classes.cardTextWrapper}>
          <div className={classes.cardTitle}>{title}</div>
          <div className={classes.cardSubtitle}>{subtitle}</div>
        </div>
      </div>
    </UnstyledButton>
  );
}

interface OmniboxQuickActionsProps {
  composeEnabled: boolean;
  onEntryFromTemplate: () => void;
  onEntryFromCompose: () => void;
  onOpenReviews: () => void;
}

export default function OmniboxQuickActions({
  composeEnabled,
  onEntryFromTemplate,
  onEntryFromCompose,
  onOpenReviews,
}: OmniboxQuickActionsProps): JSX.Element {
  const {classes} = useStyles();
  const cardCount = composeEnabled ? 3 : 2;
  return (
    <div className={classes.root}>
      <div className={classes.sectionHeader}>Quick Actions</div>
      <div
        className={classes.grid}
        style={{display: 'grid', gridTemplateColumns: `repeat(${cardCount}, 1fr)`, gap: 10}}
      >
        <QuickActionCard
          icon={<BenchlingIcon icon="file-lines" style={{fontSize: 25}} />}
          iconBgColor="#dbeafe"
          iconColor="#2563eb"
          title="New templated entry"
          subtitle="Create from template"
          onClick={onEntryFromTemplate}
        />
        {composeEnabled && (
          <QuickActionCard
            icon={<BenchlingIcon icon="sparkle" style={{fontSize: 25}} />}
            iconBgColor="#f3e8ff"
            iconColor="#7c3aed"
            title="New entry using AI"
            subtitle="Prompt-based creation"
            onClick={onEntryFromCompose}
          />
        )}
        <QuickActionCard
          icon={<BenchlingIcon icon="list-check" style={{fontSize: 25}} />}
          iconBgColor={Color.ORANGE_150}
          iconColor={Color.ORANGE_350}
          title="View pending reviews"
          subtitle="Complete open tasks"
          onClick={onOpenReviews}
        />
      </div>
    </div>
  );
}
